import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { App, Table, Card, Input, Typography, Flex, Row, Col, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { revokeInvitation } from '../../../actions/prerenderUserActions';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import useKeycloakRealm from '../../../hooks/useKeycloakRealm';
import { useDeleteUserMutation } from '../../teams/teamApiSliceLegacy';
import { AppUserRoles, UpdateRoleModal, InvitationForm } from '../../teams';
import {
  useInviteUserMutation,
  useGetInvitedLoginUsersQuery,
  useUpdateLoginUserRoleMutation,
} from '../../api/appUserApiSlice';
import { useIsDisabledForRole } from '../../../components/ButtonWithRole';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '../../../components/PointInTime';
import USER_ROLES from '../../auth/userRoles';
import { getLowerTierRoles } from '../../teams/appUserRoles';
import UserActions from '../../teams/UserActions';

const { Paragraph, Title } = Typography;

const GENERIC_ROLE_UPDATE_ERROR = 'Failed to update the user role. Please try again.';

/**
 * The sentence to show for a refused role change. Only a 4xx can name a reason —
 * a 5xx body carries the bare "Internal server error". This API's own exceptions
 * put the sentence in `data.error` and send no `message`, while the framework ones
 * put it in `data.message` and leave the reason phrase ("Forbidden") in `error`,
 * so `message` is read first. A validation failure sends an array of field
 * messages and falls through to the generic sentence.
 */
const roleUpdateMessage = ({ status, data } = {}) => {
  if (!(status >= 400 && status < 500)) return GENERIC_ROLE_UPDATE_ERROR;
  if (typeof data?.message === 'string') return data.message;
  if (data?.message === undefined && typeof data?.error === 'string') return data.error;

  return GENERIC_ROLE_UPDATE_ERROR;
};

const TeamMembersPage = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [searchTerm, setSearchTerm] = useState('');
  const [isRevokingInvite, setIsRevokingInvite] = useState(false);
  const [userBeingUpdated, setUserBeingUpdated] = useState(null);
  const prerenderUser = usePrerenderUser();
  const dispatch = useDispatch();
  const kcRealm = useKeycloakRealm();
  const invitationDisabled = useIsDisabledForRole(USER_ROLES.GUEST);

  const shouldDisplayRoles = ['prerender', 'prerender-staging'].includes(kcRealm);
  const [deleteUser, deleteUserStatus] = useDeleteUserMutation();
  const [inviteUser, inviteUserStatus] = useInviteUserMutation();
  const [updateUserRole, updateUserRoleStatus] = useUpdateLoginUserRoleMutation();
  const {
    data: invitedLoginUsers = [],
    isFetching: isFetchingInvitedUsers,
    refetch: listInvitedUsers,
  } = useGetInvitedLoginUsersQuery('teams-page', { refetchOnMountOrArgChange: true });

  const doRevokeInvitationAndRefresh = (email) => {
    setIsRevokingInvite(true);
    dispatch(revokeInvitation(email))
      .then(listInvitedUsers)
      .catch((e) => console.error(e))
      .finally(() => setIsRevokingInvite(false));
  };

  const doDeleteUserAndRefresh = (email) => {
    deleteUser(email)
      .then(listInvitedUsers)
      .catch((e) => console.error(e));
  };

  const invitedUsersData = useMemo(
    () =>
      invitedLoginUsers
        .filter((item) => item.email.includes(searchTerm.toLowerCase()))
        .map((user) => ({
          ...user,
          invitedAtTime: user.invitedAt ? Number(user.invitedAt) : null,
          joinedAtTime: user.joinedAt && user.status !== 'pending' ? Number(user.joinedAt) : null,
        })),
    [invitedLoginUsers, searchTerm]
  );

  const onSearchChange = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
  };

  const onUpdateUserRole = (data) => {
    // .unwrap() so a failed mutation rejects (without it the promise resolves to
    // { error } and the catch never runs) — surface the error and keep the modal
    // open instead of closing it silently.
    updateUserRole(data)
      .unwrap()
      .then(() => setUserBeingUpdated(null))
      // Some refusals are permanent and name what the user must do instead — an
      // account must keep one Account Owner (PRE-3460). "Please try again" is
      // wrong advice there, so prefer the sentence the API sent.
      .catch((e) => message.error(roleUpdateMessage(e)));
  };

  const inviteUserResult = useMemo(() => {
    if (inviteUserStatus.isError) return inviteUserStatus.error;

    if (inviteUserStatus.isSuccess) return { email: 'success' };

    return {};
  }, [inviteUserStatus.isError, inviteUserStatus.isSuccess, inviteUserStatus.error]);

  const isTableLoading = isFetchingInvitedUsers || deleteUserStatus.isLoading;

  const lowerTierRoles = React.useMemo(() => getLowerTierRoles(prerenderUser.role), [prerenderUser.role]);
  const getCanEditUser = (invitedUserRole) => lowerTierRoles.some(({ value }) => value === invitedUserRole);

  const renderUserActions = (record) => {
    const isPending = record.status === 'pending';
    const canEditUser = getCanEditUser(record.role);
    const hasAdminRole = prerenderUser.role === USER_ROLES.SUPER_ADMIN || prerenderUser.role === USER_ROLES.ADMIN;
    return (
      <UserActions
        onResendInviteClick={
          canEditUser && isPending ? () => inviteUser({ email: record.email, role: record.role }) : null
        }
        onUpdateUserClick={hasAdminRole && canEditUser ? () => setUserBeingUpdated(record) : null}
        onRevokeInvitationClick={
          hasAdminRole && canEditUser && isPending ? () => doRevokeInvitationAndRefresh(record.email) : null
        }
        revokeInvitationLoading={isRevokingInvite}
        onDeleteUserClick={
          hasAdminRole && canEditUser && !isPending ? () => doDeleteUserAndRefresh(record.email) : null
        }
      />
    );
  };

  return (
    <>
      <UpdateRoleModal
        open={!!userBeingUpdated}
        userBeingUpdated={userBeingUpdated}
        onClose={() => setUserBeingUpdated(null)}
        onSubmit={onUpdateUserRole}
        isLoading={updateUserRoleStatus.isLoading}
        userRole={prerenderUser.role}
      />
      <Title level={5}>Invite Team Members</Title>
      <Paragraph type="secondary">Invite unlimited users at no extra cost.</Paragraph>

      <Row gutter={[12, 24]}>
        <Col xs={24} xxl={20}>
          <Card size="small">
            <Flex vertical gap="small">
              <Input
                placeholder="Please enter the email address"
                allowClear
                onChange={onSearchChange}
                onPressEnter={onSearchChange}
                value={searchTerm}
                prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
              />
              <Table
                bordered
                dataSource={invitedUsersData}
                rowKey="id"
                pagination={false}
                scroll={{ x: true }}
                size="small"
                loading={isTableLoading}
              >
                <Table.Column
                  title="Email Address"
                  dataIndex="email"
                  key="email"
                  render={(_text, record) => {
                    return (
                      <span>
                        {record.email}
                        {record.email === prerenderUser.email ? ' (you)' : ''}
                      </span>
                    );
                  }}
                />
                <Table.Column title="Status" dataIndex="status" key="status" />
                {shouldDisplayRoles && (
                  <Table.Column
                    title="Role"
                    dataIndex="role"
                    key="role"
                    className="col-min"
                    render={(role) => AppUserRoles[role]}
                  />
                )}
                <Table.Column
                  title="Invited At"
                  dataIndex="invitedAtTime"
                  key="invitedAtTime"
                  width={POINT_IN_TIME_COLUMN_WIDTH}
                  render={(invitedAtTime) => <PointInTime value={invitedAtTime} />}
                />
                <Table.Column
                  title="Joined At"
                  dataIndex="joinedAtTime"
                  key="joinedAtTime"
                  width={POINT_IN_TIME_COLUMN_WIDTH}
                  render={(joinedAtTime) => <PointInTime value={joinedAtTime} />}
                />
                <Table.Column
                  title="Actions"
                  key="Actions"
                  render={(_text, record) => (
                    <div style={{ minHeight: token.controlHeight }}>
                      {record.email !== prerenderUser.email && renderUserActions(record)}
                    </div>
                  )}
                />
              </Table>
            </Flex>
          </Card>
        </Col>
        {!invitationDisabled && (
          <Col xs={24} xl={10}>
            <Card title="Invite Colleagues">
              <InvitationForm
                isLoading={inviteUserStatus.isLoading}
                invitation={inviteUserResult}
                onSubmit={inviteUser}
                selectRoles={shouldDisplayRoles}
                userRole={prerenderUser.role}
              />
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
};

export default TeamMembersPage;
